import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from 'components/card';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { MdAdd, MdDelete, MdPeople, MdChat, MdSend } from 'react-icons/md';
import { FaWhatsapp } from 'react-icons/fa';
import { Industry, WhatsappList, getAvailableSubIndustries } from './types';
import SendMessageModal from './SendMessageModal';

export default function BackOfficeWhatsappDiffusion() {
    const navigate = useNavigate();
    const [lists, setLists] = useState<WhatsappList[]>([]);
    const [industries, setIndustries] = useState<Industry[]>([]);
    const [loadingLists, setLoadingLists] = useState(true);

    // Create list form
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [newListName, setNewListName] = useState('');
    const [newListDescription, setNewListDescription] = useState('');
    const [newListIndustries, setNewListIndustries] = useState<string[]>([]);
    const [newListSubIndustries, setNewListSubIndustries] = useState<string[]>([]);
    const [creatingList, setCreatingList] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
    const [showSendModal, setShowSendModal] = useState(false);

    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    const fetchLists = async () => {
        try {
            setLoadingLists(true);
            const res = await axios.get(`${CONFIG.apiUrl}/admin/whatsapp-lists`, { headers });
            setLists(res.data.lists);
        } catch (e) {
            console.error(e);
        } finally {
            setLoadingLists(false);
        }
    };

    const fetchIndustries = async () => {
        try {
            const res = await axios.get(`${CONFIG.apiUrl}/admin/industries-list`, { headers });
            setIndustries(res.data.industries);
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        fetchLists();
        fetchIndustries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCreateList = async () => {
        if (!newListName.trim()) return;
        try {
            setCreatingList(true);
            const res = await axios.post(`${CONFIG.apiUrl}/admin/whatsapp-lists`, {
                name: newListName.trim(),
                description: newListDescription.trim(),
                industries: newListIndustries,
                subIndustries: newListSubIndustries,
            }, { headers });
            navigate(`/back-office/whatsapp-diffusion/${res.data.list._id}`);
        } catch (e) {
            console.error(e);
            alert('Failed to create list');
        } finally {
            setCreatingList(false);
        }
    };

    const handleDeleteList = async (id: string) => {
        try {
            await axios.delete(`${CONFIG.apiUrl}/admin/whatsapp-lists/${id}`, { headers });
            setLists((prev) => prev.filter((l) => l._id !== id));
            setDeleteConfirm(null);
        } catch (e) {
            console.error(e);
            alert('Failed to delete list');
        }
    };

    const toggleNewIndustry = (id: string) => {
        setNewListIndustries((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
        );
    };

    const sortedLists = useMemo(
        () => [...lists].sort((a, b) => b.userIds.length - a.userIds.length),
        [lists]
    );

    return (
        <div className="pt-[50px]">
            {/* Header */}
            <Card extra="p-4 mb-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <FaWhatsapp className="text-2xl text-green-500" />
                        <span className="text-lg font-bold text-navy-700 dark:text-white">
                            Listes diffusion
                        </span>
                        {!loadingLists && (
                            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500 dark:bg-navy-700 dark:text-gray-300">
                                {lists.length}
                            </span>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => navigate('/back-office/whatsapp-templates')}
                            className="flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 dark:border-navy-600 dark:text-gray-300 dark:hover:bg-navy-700"
                        >
                            <MdChat />
                            Templates
                        </button>
                        <button
                            onClick={() => setShowCreateForm(!showCreateForm)}
                            className="flex items-center justify-center gap-2 rounded-lg border border-green-500 px-4 py-2 text-sm font-medium text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20"
                        >
                            <MdAdd />
                            Nouvelle liste
                        </button>
                        <button
                            onClick={() => setShowSendModal(true)}
                            className="flex items-center justify-center gap-2 rounded-lg bg-green-500 px-4 py-2 text-sm font-medium text-white hover:bg-green-600"
                        >
                            <MdSend />
                            Envoyer un message
                        </button>
                    </div>
                </div>

                {showCreateForm && (
                    <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-navy-600 dark:bg-navy-800">
                        <input
                            type="text"
                            placeholder="Nom de la liste *"
                            value={newListName}
                            onChange={(e) => setNewListName(e.target.value)}
                            className="mb-2 w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm focus:border-green-400 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                        />
                        <textarea
                            placeholder="Description (optionnel)"
                            value={newListDescription}
                            onChange={(e) => setNewListDescription(e.target.value)}
                            rows={2}
                            className="mb-2 w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm focus:border-green-400 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                        />
                        {industries.length > 0 && (
                            <div className="mb-2">
                                <div className="mb-1 text-xs font-medium text-gray-500 dark:text-gray-400">
                                    Industries (tags)
                                </div>
                                <div className="flex flex-wrap gap-1">
                                    {industries.map((ind) => (
                                        <button
                                            key={ind._id}
                                            onClick={() => toggleNewIndustry(ind._id)}
                                            className={`rounded px-2 py-0.5 text-xs ${
                                                newListIndustries.includes(ind._id)
                                                    ? 'bg-green-500 text-white'
                                                    : 'bg-gray-200 text-gray-700 dark:bg-navy-600 dark:text-gray-300'
                                            }`}
                                        >
                                            {ind.name}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        {newListIndustries.length > 0 && (
                            <div className="mb-2">
                                <div className="mb-1 text-xs font-medium text-gray-500 dark:text-gray-400">
                                    Sous-industries
                                </div>
                                <div className="flex flex-wrap gap-1">
                                    {getAvailableSubIndustries(industries, newListIndustries).map((sub) => (
                                        <button
                                            key={sub}
                                            onClick={() =>
                                                setNewListSubIndustries((prev) =>
                                                    prev.includes(sub)
                                                        ? prev.filter((s) => s !== sub)
                                                        : [...prev, sub]
                                                )
                                            }
                                            className={`rounded px-2 py-0.5 text-xs ${
                                                newListSubIndustries.includes(sub)
                                                    ? 'bg-green-500 text-white'
                                                    : 'bg-gray-200 text-gray-700 dark:bg-navy-600 dark:text-gray-300'
                                            }`}
                                        >
                                            {sub}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div className="flex gap-2">
                            <button
                                onClick={handleCreateList}
                                disabled={!newListName.trim() || creatingList}
                                className="rounded bg-green-500 px-4 py-1 text-xs font-medium text-white hover:bg-green-600 disabled:opacity-50"
                            >
                                {creatingList ? 'Création...' : 'Créer'}
                            </button>
                            <button
                                onClick={() => setShowCreateForm(false)}
                                className="rounded bg-gray-400 px-3 py-1 text-xs text-white hover:bg-gray-500"
                            >
                                Annuler
                            </button>
                        </div>
                    </div>
                )}
            </Card>

            {/* Lists grid — sorted by member count */}
            {loadingLists ? (
                <div className="py-8 text-center text-sm text-gray-400">Chargement...</div>
            ) : sortedLists.length === 0 ? (
                <Card extra="p-8">
                    <div className="text-center text-gray-400">
                        <FaWhatsapp className="mx-auto mb-3 text-5xl text-gray-300" />
                        <p className="text-sm">Aucune liste</p>
                    </div>
                </Card>
            ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {sortedLists.map((list) => {
                        const count = list.userIds.length;
                        return (
                            <div
                                key={list._id}
                                onClick={() => navigate(`/back-office/whatsapp-diffusion/${list._id}`)}
                                className="group relative flex cursor-pointer flex-col rounded-[20px] border-2 border-transparent bg-white p-4 shadow-sm transition-all hover:border-green-200 hover:shadow-md dark:bg-navy-800 dark:hover:border-navy-600"
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                        <div className="truncate text-base font-bold text-navy-700 dark:text-white">
                                            {list.name}
                                        </div>
                                        <div className="text-xs text-gray-400">
                                            Créée le {new Date(list.createdAt).toLocaleDateString('fr-FR')}
                                        </div>
                                    </div>
                                    <div onClick={(e) => e.stopPropagation()}>
                                        {deleteConfirm === list._id ? (
                                            <div className="flex gap-1">
                                                <button
                                                    onClick={() => handleDeleteList(list._id)}
                                                    className="rounded bg-red-500 px-1.5 py-0.5 text-xs text-white"
                                                >
                                                    Oui
                                                </button>
                                                <button
                                                    onClick={() => setDeleteConfirm(null)}
                                                    className="rounded bg-gray-400 px-1.5 py-0.5 text-xs text-white"
                                                >
                                                    Non
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                onClick={() => setDeleteConfirm(list._id)}
                                                className="rounded p-1 text-red-400 opacity-0 hover:bg-red-50 hover:text-red-600 group-hover:opacity-100 dark:hover:bg-red-900/20"
                                            >
                                                <MdDelete />
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {list.description && (
                                    <p className="mt-2 line-clamp-2 text-sm text-gray-500 dark:text-gray-400">
                                        {list.description}
                                    </p>
                                )}

                                {(list.industries.length > 0 || list.subIndustries.length > 0) && (
                                    <div className="mt-3 flex flex-wrap gap-1">
                                        {list.industries.map((ind) => (
                                            <span
                                                key={ind._id}
                                                className="rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                                            >
                                                {ind.name}
                                            </span>
                                        ))}
                                        {list.subIndustries.slice(0, 3).map((sub) => (
                                            <span
                                                key={sub}
                                                className="rounded-full bg-purple-100 px-2 py-0.5 text-xs text-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
                                            >
                                                {sub}
                                            </span>
                                        ))}
                                        {list.subIndustries.length > 3 && (
                                            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500 dark:bg-navy-700 dark:text-gray-300">
                                                +{list.subIndustries.length - 3}
                                            </span>
                                        )}
                                    </div>
                                )}

                                <div className="flex-1" />
                                <div className="mt-3 flex items-center gap-2 border-t border-gray-100 pt-3 dark:border-navy-700">
                                    <MdPeople className="text-xl text-green-500" />
                                    <span className="text-2xl font-bold text-navy-700 dark:text-white">{count}</span>
                                    <span className="text-sm text-gray-400">membre{count !== 1 ? 's' : ''}</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {showSendModal && <SendMessageModal lists={lists} onClose={() => setShowSendModal(false)} />}
        </div>
    );
}
